import { observer } from "mobx-react";
import { ReactNode, useEffect, useState } from "react";
import InvitationStore, {
  BatchInvitationStore,
  invitationTypeLabel,
} from "@/stores/InvitationStore";
import { Button, LinkButton } from "@/components-v2/Button";
import { Alert } from "@/components-v2/Alert";
import { DcompanyLogoImg } from "@/components-v2/Logo";
import styles from "@/Login/LoginComponents.module.css";
import { dcompanyFrontpageUrl, navigateToRoot } from "@/utils/navigation";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { useLoginModel } from "@/Login/loginDomain";
import { Box, Container, VBox } from "@/components-v2/Box";
import { useLanguage, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { Icon } from "@/components-v2/Icon";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { isMobileView } from "@/utils/screen";
import { Markdown } from "@/text/Markdown";
import { LanguageSelector } from "@/components-v2/LanguageSelector";
import { LoginView } from "@/Login/loginComponents";

const InvalidInvitation = observer(function UserInvitationView() {
  const { t } = useTranslation();
  const history = useRootStore().history;
  return (
    <Container gap={"lg"} alignItems={"center"} paddingX={false}>
      <Box display={"flex"} justifyContent={"center"}>
        <DcompanyLogoImg full className={styles.logo} />
      </Box>
      <p>{t("invitation.invalid-invitation")}</p>
      <p>
        <LinkButton label={t("invitation.gotoPortfolio")} onClick={() => navigateToRoot(history)} />
      </p>
    </Container>
  );
});

const AcceptInvitation = observer(function AcceptInvitation({
  store,
  invitationLabel,
}: {
  store: InvitationStore;
  invitationLabel?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Container gap={"lg"} alignItems={"center"} paddingX={false}>
      {invitationLabel}
      <p>
        <Button
          variant={"primary"}
          label={t("invitation.accept-invitation")}
          loading={store.isAcceptLoading}
          disabled={store.isAcceptError}
          onClick={store.acceptInvitation}
        />
      </p>
      {store.isAcceptError && <Alert type="error" message={t("invitation.accept-error")} />}
    </Container>
  );
});

const UserInvitationView = observer(function UserInvitationView({
  store,
}: {
  store: InvitationStore;
}) {
  const { t } = useTranslation();
  const invitationLabel = store.invitation && (
    <VBox textAlign={"center"} gap={"lg"}>
      <h4>{t("invitation.join-dcompany", { invitedByName: store.invitation.invitedByName })}</h4>
      <Box>{t(invitationTypeLabel(store.invitation))}</Box>
      {!store.isLoggedIn && (
        <VBox textAlign={"center"} gap={false}>
          <strong>{t("invitation.accept-by-logging-in-with-bankid")}</strong>
          {t("invitation.accept-by-logging-in-with-bankid.description")}
        </VBox>
      )}
    </VBox>
  );
  const loginModel = useLoginModel({
    disableLogo: true,
    disableIntroText: true,
    canCreateUser: store.invitation?.hasExistingUser !== true,
    username: store.invitation?.recipientEmail,
    invitation: store.createUserInvitation,
  });
  return (
    <>
      {store.isLoggedIn ? (
        <AcceptInvitation store={store} invitationLabel={invitationLabel} />
      ) : (
        <LoginView model={loginModel} customHeading={invitationLabel} />
      )}
    </>
  );
});

const CompanyInvitationView = observer(function CompanyInvitationView({
  store,
}: {
  store: InvitationStore;
}) {
  const { t } = useTranslation();
  const invitationLabel = store.invitation && (
    <p>
      <strong>{store.invitation.invitedByName}</strong> ({store.invitation.invitedByEmail}){" "}
      {t("invitation.has-invited-you-and-your-company")}{" "}
      <strong>{store.invitation.companyName}</strong> {t("invitation.to-join")}{" "}
      <strong>dCompany</strong>.
    </p>
  );
  const loginModel = useLoginModel({
    disableLogo: true,
    disableIntroText: true,
    disableUsernamePasswordLogin: true,
  });

  return (
    <>
      {store.isLoggedIn ? (
        <AcceptInvitation store={store} invitationLabel={invitationLabel} />
      ) : (
        <LoginView model={loginModel} customHeading={invitationLabel} />
      )}
    </>
  );
});

export const InvitationHeader = observer(({ companyName }: { companyName?: string }) => {
  const lang = useLanguage();
  const { t } = useTranslation();
  return isMobileView() ? (
    <Box justifyContent={"space-between"} borderBottom paddingY>
      <div>
        <a href={dcompanyFrontpageUrl(lang)} target="_blank">
          <DcompanyLogoImg full className={styles.logoSmall} />
        </a>
      </div>
      <Box alignItems={"center"} marginRight={"lg"} gap={"lg"}>
        <LanguageSelector />
      </Box>
    </Box>
  ) : (
    <Box justifyContent={"space-between"} borderBottom padding>
      <Box alignItems={"center"}>
        <div>
          <a href={dcompanyFrontpageUrl(lang)} target="_blank">
            <DcompanyLogoImg full className={styles.logoSmall} />
          </a>
        </div>
        {companyName && <div>{companyName}</div>}
      </Box>
      <Box alignItems={"center"} marginRight={"lg"} gap={"lg"}>
        <Markdown value={t("about-dcompany")} enableLinks />
        <LanguageSelector />
      </Box>
    </Box>
  );
});

const InvitationPage = observer(function InvitationPage({
  invitationId,
  invitationToken,
}: {
  invitationId: string;
  invitationToken: string;
}) {
  const { apiService, userStore, messageStore, history } = useRootStore();
  const [invitationStore] = useState(
    () =>
      new InvitationStore(
        userStore,
        invitationId,
        invitationToken,
        apiService,
        messageStore,
        history,
      ),
  );

  useEffect(() => {
    invitationStore.fetchInvitation();
  }, [invitationStore]);

  if (invitationStore.isLoading) {
    return <LoadingIndicator fullscreen />;
  }

  return (
    <FullScreenLayout
      appBar={<InvitationHeader companyName={invitationStore.invitation?.companyName} />}
      centerPanel={
        <Container marginTop={isMobileView() ? "sm" : "md"} paddingTop={"lg"}>
          {invitationStore.isLoadError ? (
            <InvalidInvitation />
          ) : invitationStore.isCompanyInvitation ? (
            <CompanyInvitationView store={invitationStore} />
          ) : (
            <UserInvitationView store={invitationStore} />
          )}
        </Container>
      }
    />
  );
});

const InvitationText = observer(({ store }: { store: BatchInvitationStore }) => {
  const { t } = useTranslation();

  return !store.invitation ? null : (
    <VBox>
      <Markdown
        value={t("invitation.has-invited-you-to-their-company-batch", {
          invitedByName: store.invitation.invitedByName,
        })}
        enableLinks
      />
      <VBox alignItems={"start"} padding>
        {store.invitation.companyNames.map((companyName) => (
          <Box key={companyName} alignItems={"center"}>
            <Icon name={"check-circle"} size="sm" /> {companyName}
          </Box>
        ))}
      </VBox>
    </VBox>
  );
});

export const BatchInvitationPage = observer(function BatchInvitationPage({
  batchInvitationId,
  invitationToken,
}: {
  batchInvitationId: string;
  invitationToken: string;
}) {
  const { t } = useTranslation();
  const { apiService, userStore, history } = useRootStore();
  const [store] = useState(
    () =>
      new BatchInvitationStore(userStore, batchInvitationId, invitationToken, apiService, history),
  );
  const loginModel = useLoginModel({
    disableIntroText: true,
    disableLogo: true,
    canCreateUser: store.invitation?.hasExistingUser !== true,
    username: store.invitation?.recipientEmail,
    invitation: store.createUserInvitation,
  });

  useEffect(() => {
    store.fetchBatchInvitation();
  }, [store]);

  switch (true) {
    case store.isLoading:
      return <LoadingIndicator fullscreen />;
    case store.isLoadError:
      return (
        <FullScreenLayout
          appBar={<InvitationHeader />}
          centerPanel={
            <Container marginTop={isMobileView() ? "sm" : "md"} paddingTop={"lg"}>
              <InvalidInvitation />
            </Container>
          }
        />
      );
    case store.isLoggedIn:
      return (
        <FullScreenLayout
          appBar={<InvitationHeader />}
          centerPanel={
            <Container alignItems={"center"} paddingTop={"xl"}>
              <InvitationText store={store} />
            </Container>
          }
          footerPanel={
            <VBox padding borderTop alignItems={"center"}>
              <Button
                variant={"primary"}
                label={t("invitation.accept-invitation")}
                loading={store.isAcceptLoading}
                disabled={store.isAcceptError}
                onClick={store.acceptInvitation}
              />
            </VBox>
          }
        />
      );
    default:
      return (
        <LoginView
          model={loginModel}
          customHeading={
            <div className="text-center">
              <Markdown value={t("invitation.login-to-confirm")} />
            </div>
          }
        />
      );
  }
});

export default InvitationPage;
